import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  API,
  authHeaders,
  authConfig,
  alertError,
  alertSuccess,
  btnPrimary,
  fieldStyle,
  labelStyle,
  pageTitleStyle,
  pageSubtitleStyle,
} from '../api/ticketsApi';
import TicketStatusBadge from '../components/TicketStatusBadge';
import TicketNoLookup from '../components/TicketNoLookup';
import { usePrefillTicketNo } from '../hooks/usePrefillTicketNo';
import { TicketActionBack } from '../ticketNav';
import { useProductFilter } from '../ProductFilterContext';

function TicketReopenPage() {
  const prefillTicket = usePrefillTicketNo();
  const { productId, selectedProduct } = useProductFilter();
  const [tab, setTab] = useState('reopen');
  const [ticket, setTicket] = useState(null);
  const [ticketNo, setTicketNo] = useState(prefillTicket || '');
  const [engineers, setEngineers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [prefillReady, setPrefillReady] = useState(!prefillTicket);

  const [reopenReason, setReopenReason] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [assigneeIds, setAssigneeIds] = useState([]);

  // From ticket detail: pick reopen vs close tab from current status.
  useEffect(() => {
    if (!prefillTicket) {
      setPrefillReady(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/${encodeURIComponent(prefillTicket)}`, authHeaders());
        if (cancelled) return;
        const st = res.data?.status;
        if (st === 'reopen') {
          setTab('close');
        } else {
          setTab('reopen');
        }
        setTicketNo(prefillTicket);
      } catch {
        setTab('reopen');
        setTicketNo(prefillTicket);
      } finally {
        if (!cancelled) setPrefillReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [prefillTicket]);

  useEffect(() => {
    if (tab !== 'reopen' || !ticket?.ticket_no) {
      setEngineers([]);
      return undefined;
    }
    let cancelled = false;
    axios.get(`${API}/assignments/engineers`, authConfig({ ticket_no: ticket.ticket_no }))
      .then((res) => { if (!cancelled) setEngineers(res.data || []); })
      .catch(() => { if (!cancelled) setEngineers([]); });
    return () => { cancelled = true; };
  }, [tab, ticket?.ticket_no]);

  const statusFilter = tab === 'reopen'
    ? (t) => t?.status === 'closed'
    : (t) => t?.status === 'reopen';

  const resetTicketFields = () => {
    setTicket(null);
    setTicketNo('');
    setReopenReason('');
    setCloseReason('');
    setAssigneeIds([]);
  };

  const toggleAssignee = (userId) => {
    setAssigneeIds((ids) => (
      ids.includes(userId) ? ids.filter((id) => id !== userId) : [...ids, userId]
    ));
  };

  const handleReopen = async (e) => {
    e.preventDefault();
    const no = ticket?.ticket_no || ticketNo.trim();
    if (!no || !reopenReason.trim()) {
      setError('Ticket and reason are required');
      return;
    }
    if (!ticket || ticket.status !== 'closed') {
      setError('Lookup a closed ticket before reopening');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.post(
        `${API}/lifecycle/reopen`,
        {
          ticket_no: no,
          reason: reopenReason,
          assignee_ids: assigneeIds.length ? assigneeIds : null,
        },
        authHeaders(),
      );
      setSuccess(`Ticket ${no} reopened`);
      resetTicketFields();
    } catch (err) {
      setError(err.response?.data?.detail || 'Reopen failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async (e) => {
    e.preventDefault();
    const no = ticket?.ticket_no || ticketNo.trim();
    if (!no || !closeReason.trim()) {
      setError('Ticket and reason are required');
      return;
    }
    if (!ticket || ticket.status !== 'reopen') {
      setError('Lookup a reopened ticket before closing');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.post(
        `${API}/lifecycle/${encodeURIComponent(no)}/close`,
        { reason: closeReason },
        authHeaders(),
      );
      setSuccess(`Ticket ${no} closed`);
      resetTicketFields();
    } catch (err) {
      setError(err.response?.data?.detail || 'Close failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTabChange = (next) => {
    setTab(next);
    setTicket(null);
    setError(null);
    setSuccess(null);
    setAssigneeIds([]);
    // Keep typed/prefilled number so lookup re-runs against the new status filter.
  };

  if (!prefillReady) {
    return <p style={{ color: '#74777F' }}>Preparing…</p>;
  }

  return (
    <div style={{ maxWidth: '720px' }}>
      <div className="tk-page-head">
        <div className="tk-page-head-main">
          <div className="tk-page-head-back">
            <TicketActionBack ticketNo={ticket?.ticket_no || ticketNo || prefillTicket || null} />
          </div>
          <h2 style={pageTitleStyle}>Reopen / Close</h2>
          <p style={pageSubtitleStyle}>
            Enter a ticket number to load that ticket only — no full list download.
            {selectedProduct ? ` Scoped to ${selectedProduct.name}.` : ''}
          </p>
        </div>
      </div>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}
      {success && <div style={alertSuccess}>{success}</div>}

      <div className="tk-segmented" style={{ marginBottom: '14px' }}>
        <button type="button" className={tab === 'reopen' ? 'is-active' : ''} onClick={() => handleTabChange('reopen')}>Reopen</button>
        <button type="button" className={tab === 'close' ? 'is-active' : ''} onClick={() => handleTabChange('close')}>Close</button>
      </div>

      {tab === 'reopen' ? (
        <form onSubmit={handleReopen} style={{ display: 'grid', gap: '14px' }}>
          <TicketNoLookup
            key={`reopen-${tab}-${productId || 'all'}`}
            value={ticketNo}
            onChange={(v) => {
              setTicketNo(v);
              setSuccess(null);
              setError(null);
              setAssigneeIds([]);
            }}
            onTicket={setTicket}
            filter={statusFilter}
            productId={productId}
            label="Closed ticket number"
            placeholder="Type closed ticket no…"
          />

          {ticket && (
            <div className="tk-field-readonly" style={{ padding: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{ticket.ticket_no}</strong>
                <TicketStatusBadge status={ticket.status} />
              </div>
              <p style={{ margin: '8px 0 0', fontSize: '13px', fontWeight: 500 }}>{ticket.subject}</p>
            </div>
          )}

          <div>
            <label className="tk-label" style={labelStyle}>Reason *</label>
            <textarea
              className="tk-field"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              rows={3}
              required
              style={fieldStyle}
            />
          </div>
          <div>
            <label className="tk-label" style={labelStyle}>Reassign to (optional)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {engineers.length === 0 ? (
                <span style={{ color: '#74777F', fontSize: '13px' }}>
                  {ticket ? 'No assignees available for this ticket.' : 'Lookup a ticket to load team members.'}
                </span>
              ) : engineers.map((eng) => {
                const userId = eng.user_id || eng;
                const name = eng.name || eng;
                return (
                  <label
                    key={userId}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      padding: '8px 12px',
                      borderRadius: '999px',
                      border: assigneeIds.includes(userId) ? '2px solid #002FA7' : '1px solid #C9CED8',
                      background: assigneeIds.includes(userId) ? '#EEF0FF' : '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={assigneeIds.includes(userId)}
                      onChange={() => toggleAssignee(userId)}
                    />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting || !ticket}
            className="tk-btn tk-btn-primary"
            style={{ ...btnPrimary, width: 'fit-content' }}
          >
            {submitting ? 'Reopening…' : 'Reopen Ticket'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleClose} style={{ display: 'grid', gap: '14px' }}>
          <TicketNoLookup
            key={`close-${tab}-${productId || 'all'}`}
            value={ticketNo}
            onChange={(v) => {
              setTicketNo(v);
              setSuccess(null);
              setError(null);
            }}
            onTicket={setTicket}
            filter={statusFilter}
            productId={productId}
            label="Reopened ticket number"
            placeholder="Type reopened ticket no…"
          />

          {ticket && (
            <div className="tk-field-readonly" style={{ padding: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{ticket.ticket_no}</strong>
                <TicketStatusBadge status={ticket.status} />
              </div>
              <p style={{ margin: '8px 0 0', fontSize: '13px', fontWeight: 500 }}>{ticket.subject}</p>
            </div>
          )}

          <div>
            <label className="tk-label" style={labelStyle}>Close Reason *</label>
            <textarea
              className="tk-field"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              rows={3}
              required
              style={fieldStyle}
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !ticket}
            className="tk-btn tk-btn-primary"
            style={{ ...btnPrimary, width: 'fit-content' }}
          >
            {submitting ? 'Closing…' : 'Close Ticket'}
          </button>
        </form>
      )}
    </div>
  );
}

export default TicketReopenPage;
